export type AudioLevelController = {
  stop: () => Promise<void>;
};

/**
 * Starts an audio level meter from a given MediaStream and reports levels via callback.
 * Returns a controller with a stop() method to cleanup.
 */
export function startAudioLevel(
  stream: MediaStream,
  onLevel: (level: number) => void
): AudioLevelController | null {
  try {
    const AudioContextCtor: typeof AudioContext = window.AudioContext;
    if (!AudioContextCtor) {
      console.warn('AudioContext is not supported in this browser.');
      return null;
    }

    const audioCtx: AudioContext = new AudioContextCtor();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();

    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);

    const timeData = new Uint8Array(analyser.fftSize);
    let rafId: number | null = null;

    const update = () => {
      analyser.getByteTimeDomainData(timeData);
      let sum = 0;
      for (let i = 0; i < timeData.length; i++) {
        const v = (timeData[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / timeData.length);
      const level = Math.min(1, rms * 1.5);
      onLevel(level);
      rafId = requestAnimationFrame(update);
    };

    // Some browsers require resume on user gesture; ignore failures
    audioCtx.resume().catch(() => {});
    rafId = requestAnimationFrame(update);

    const stop = async () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      try {
        analyser.disconnect();
      } catch {
        /* empty */
      }
      try {
        await audioCtx.close();
      } catch {
        /* empty */
      }
      onLevel(0);
    };

    return { stop };
  } catch (err) {
    console.warn('Failed to start audio level meter', err);
    return null;
  }
}
